import { customSeriesDefaultOptions } from 'lightweight-charts'

/**
 * SegmentsSeries — N primitivas horizontales en UNA sola custom series.
 *
 * Motivación: Liquidity Pools Quantum pinta sus pools como bandas (canon
 * v1.0) y cada una con una BoxSeries propia multiplicaría las series overlay
 * (histórico: >1000 series, "Value is null" diferido). Aquí todos los items
 * de un tipo se dibujan en un único canvas pass.
 *
 * El indicador inyecta los items con `setSegments()` antes de `setData()`:
 *   - Segmento:  { time0, time1, price, color, alpha, width, lineStyle }
 *   - Banda:     { kind: 'band', time0, time1, time2, upper, lower, color,
 *                  fillAlpha, borderAlpha, borderWidth, borderStyle,
 *                  project, projectStyle, projBars }
 *                Tramo histórico time0→time1 (formación → última vela cerrada)
 *                con borde `borderStyle`; proyección time1→time2 (última vela
 *                → fin del rightOffset del chart, `projBars` barras) con borde
 *                `projectStyle`. Sin `project` la banda termina en time1 con
 *                cierre derecho (zona terminada, p. ej. pool barrido en su
 *                sweep_time). La x de time2 se resuelve con la misma
 *                extrapolación por tiempo que el resto (xOf(time2)), SIN crear
 *                una fila en el futuro (no se contamina el time scale con
 *                timestamps sin vela); `projBars` queda como contrato/tests.
 *   - Triángulo: { kind: 'triangle', time, price, direction: 'up'|'down',
 *                  color, size } — marcador pequeño fuera de la vela: 'down'
 *                (▼) con el ápice apuntando al high, 'up' (▲) al low (CISD).
 *
 * ANCLAJE TEMPORAL (canon §3.1): las bandas van de su barra de formación al
 * límite del rightOffset, NUNCA de borde a borde del viewport. La x se
 * resuelve así (ronda 3.2):
 *  - Anclas: solo filas con x finita E índice dentro del rango de ítems
 *    visible [from, to) — LWC las recalcula frescas cada frame; fuera del
 *    rango conserva x OBSOLETA (no NaN), que se ignora siempre (sonda).
 *  - pps (px/segundo): fuente primaria `data.barSpacing ÷ barSeconds`
 *    (barSpacing lo entrega LWC fresco por frame; barSeconds lo fija el
 *    indicador con setTimeBase). Es la única fuente inmune a la recreación
 *    por tick en vivo de las páginas (~350 ms → SegmentsSeries nuevo →
 *    cache muerta). Fallbacks: pps de pares trusted → cache.
 *  - El resto se EXTRAPOLA linealmente por TIEMPO desde la ancla más
 *    cercana (x es lineal en tiempo en un time scale de timestamps
 *    uniformes). La serie es DISPERSA (una fila por extremo de
 *    banda/triángulo), así que la extrapolación por índice de ítem está
 *    PROHIBIDA: Δidx=1 puede ser decenas de barras (bug de las bandas
 *    "infinitas"/fantasma, ronda 2 — la proyección crecía al panear y bandas
 *    formadas fuera de la ventana aparecían dentro del viewport). Solo con 0
 *    anclas se clipea por LADO (índice de ítem vs `visibleRange`), que
 *    es el caso de una banda atravesando el viewport; los triángulos con x
 *    de clamp no se dibujan. Nada se clipea a 0 ni a paneWidth teniendo
 *    referencia temporal: una banda cuya formación queda fuera de pantalla
 *    empieza en x<0 (el canvas recorta) y al panear al vacío futuro la
 *    proyección TERMINA en el límite del rightOffset en vez de crecer.
 *    Todo se resuelve en draw() con las coordenadas del frame actual,
 *    así funciona con renderOnPan:false.
 *
 * `setData()` recibe una fila por cada tiempo extremo de algún item
 * ({ time, _t, price }) con DOS funciones:
 *   1. Mapeo time→x del time scale (cada fila duplica su time en `_t`).
 *   2. Contrato de plot values de lightweight-charts: TODA fila no-whitespace
 *      de una custom series debe producir valores de precio FINITOS vía
 *      `priceValueBuilder`. La primera versión devolvía `[]`, lo que generaba
 *      plot rows `[undefined, -Infinity, Infinity, undefined]` — cualquier
 *      camino interno de LWC que lea esos valores (firstValue, rango de la
 *      serie, evaluación eager de getVisibleRange al disparar
 *      visibleTimeRangeChange) puede terminar en "Value is null".
 */
class SegmentsPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
    this._segments = []
    this._barSeconds = null
    this._priceFn = null
  }

  setSegments(segments) {
    this._segments = Array.isArray(segments) ? segments : []
  }

  // Base temporal del chart (segundos por vela, mediana del indicador): con el
  // `barSpacing` que LWC entrega fresco por frame da la pps canónica.
  setTimeBase({ barSeconds } = {}) {
    this._barSeconds = Number.isFinite(barSeconds) && barSeconds > 0 ? barSeconds : null
  }

  // Ancla de PRECIO (ronda 3.3): en ventanas sin velas visibles el
  // priceConverter que LWC entrega al custom series diverge del eje (escala
  // obsoleta/alternativa con autoscaleInfoProvider null). El indicador
  // inyecta el priceToCoordinate de la SERIE DE VELAS — la escala viva que
  // dibuja el eje — consultado por frame. Puede devolver null fuera de
  // rango: se trata como dato inválido (salta el item, regla §4.2).
  setPriceFn(fn) {
    this._priceFn = typeof fn === 'function' ? fn : null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!bars || bars.length === 0 || this._segments.length === 0) return
    // Y anclada a la escala viva de las velas si el indicador la inyectó;
    // fallback al convertidor LWC (tests/fixtures).
    const pc = this._priceFn ?? priceConverter

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const paneWidth = scope.mediaSize?.width ?? 0
      const paneHeight = scope.mediaSize?.height ?? 0

      // Anclas de confianza (canon §3.1, ronda 3.2). Sonda empírica sobre LWC
      // 4.2.3: para filas FUERA del rango visible, bar.x NO es NaN
      // necesariamente — LWC conserva coordenadas OBSOLETAS de frames
      // anteriores (valores duplicados o no monótonos entre frames), mientras
      // que las filas DENTRO del rango de ítems visible [from, to) siempre se
      // RECALCULAN frescas (demostrado: la fila de la última vela recibe su x
      // correcta en el vacío futuro aunque las vecinas lleven x obsoleta).
      // Reglas:
      //   1. Ancla TRUSTED: fila con x finita E índice dentro de [from, to)
      //      (to exclusivo). Su x se usa tal cual; la de fuera se ignora.
      //   2. pps (px/segundo): fuente primaria `barSpacing ÷ barSeconds`
      //      (barSpacing lo entrega LWC fresco por frame; es la única vía
      //      inmune a la recreación por tick en vivo, que mata la cache).
      //      Fallbacks: pares trusted → cache (jamás de valores obsoletos).
      //   3. El resto se extrapola por TIEMPO desde el trusted más cercano
      //      (x es lineal en tiempo en un time scale de timestamps
      //      uniformes); con 0 trusted → clamp por LADO (índice de ítem vs
      //      visibleRange), y los triángulos con x de clamp NO se dibujan.
      const rows = [] // { t, i, x } en orden de tiempo (las filas van ordenadas)
      const idxByTime = new Map()
      let firstRowTime = null
      let lastRowTime = null
      for (let i = 0; i < bars.length; i++) {
        const bar = bars[i]
        const t = bar?.originalData?._t
        if (t == null || !Number.isFinite(t)) continue
        idxByTime.set(t, i)
        if (firstRowTime == null || t < firstRowTime) firstRowTime = t
        if (lastRowTime == null || t > lastRowTime) lastRowTime = t
        if (Number.isFinite(bar?.x)) rows.push({ t, i, x: bar.x })
      }

      // Trusted: dentro del rango de ítems visible [from, to) — LWC las
      // recalcula frescas cada frame; fuera del rango la x es sospechosa.
      const rangeFrom = visibleRange?.from ?? null
      const rangeTo = visibleRange?.to ?? null
      const trusted = rangeFrom == null || rangeTo == null
        ? []
        : rows.filter((r) => r.i >= rangeFrom && r.i < rangeTo)
      const trustedByTime = new Map()
      for (const r of trusted) trustedByTime.set(r.t, r.x)

      // pps = píxeles por SEGUNDO. Fuente PRIMARIA: el `barSpacing` que LWC
      // entrega fresco por frame en el update del custom series ÷ barSeconds
      // (setTimeBase). Es la única fuente que sobrevive a la recreación por
      // tick en vivo de las páginas (cada tick crea un SegmentsSeries nuevo y
      // la cache muere — en el vacío, con una sola ancla trusted, la cache
      // nunca llegaba a existir: bandas suprimidas o proyectadas a paneWidth).
      // Fallbacks, en orden: pps de pares trusted → cache (solo respaldo).
      const barSpacing = Number.isFinite(this._data?.barSpacing) && this._data.barSpacing > 0
        ? this._data.barSpacing
        : null
      let pps = barSpacing != null && this._barSeconds != null
        ? barSpacing / this._barSeconds
        : null
      if (pps == null && trusted.length >= 2) {
        const a = trusted[0]
        const b = trusted[trusted.length - 1]
        if (b.t > a.t) {
          const v = (b.x - a.x) / (b.t - a.t)
          if (Number.isFinite(v) && v > 0) {
            pps = v
            this._pps = v
          }
        }
      }
      if (pps == null) pps = this._pps ?? null

      // x de un tiempo anclado: coordenada real si la fila es trusted;
      // EXTRAPOLACIÓN lineal por tiempo desde el trusted más cercano si está
      // fuera (x<0 o x>paneWidth — el canvas recorta); clamp por LADO solo
      // cuando no hay ninguna ancla de la que extrapolar (banda atravesando
      // el viewport o viewport en el vacío). Devuelve { x, clamped } —
      // `clamped` marca los valores sintéticos de borde: los triángulos con
      // x de clamp no se dibujan (nada de marcadores pegados al borde).
      const sideClamp = (t) => {
        const idx = idxByTime.get(t)
        if (idx != null && rangeFrom != null && rangeTo != null) {
          if (idx < rangeFrom) return 0
          if (idx >= rangeTo) return paneWidth
          return NaN // dentro del rango de ítems pero sin ancla: no dibujar
        }
        // Sin fila propia (p. ej. time2 de la proyección): lado por tiempo.
        if (lastRowTime != null && t >= lastRowTime) return paneWidth
        if (firstRowTime != null && t <= firstRowTime) return 0
        return NaN
      }
      const xOf = (t) => {
        const xt = trustedByTime.get(t)
        if (Number.isFinite(xt)) return { x: xt, clamped: false }
        if (pps != null && trusted.length > 0) {
          let ref = trusted[0]
          for (const f of trusted) {
            if (Math.abs(f.t - t) < Math.abs(ref.t - t)) ref = f
          }
          const xEst = ref.x + (t - ref.t) * pps
          // Cota de seguridad contra coordenadas patológicas en el canvas.
          const limit = Math.max(paneWidth * 20, 20000)
          return { x: Math.max(-limit, Math.min(limit, xEst)), clamped: false }
        }
        return { x: sideClamp(t), clamped: true }
      }

      for (const seg of this._segments) {
        if (!seg) continue

        if (seg.kind === 'triangle') {
          const pos = xOf(seg.time)
          const x = pos.x
          const y = pc(seg.price)
          // Con x de clamp (sin ancla trusted) el marcador no se dibuja:
          // nada de triángulos pegados al borde en frames sin referencia.
          if (pos.clamped) continue
          if (!Number.isFinite(x) || y == null || Number.isNaN(y)) continue
          if (x < -20 || x > paneWidth + 20) continue
          if (y < -paneHeight || y > paneHeight * 2) continue
          const size = Number.isFinite(seg.size) ? seg.size : 5
          const gap = 3
          ctx.beginPath()
          ctx.fillStyle = this._withAlpha(seg.color || '#B08D57', seg.alpha ?? 1)
          if (seg.direction === 'down') {
            // ▼ sobre el high: ápice apuntando a la vela, base hacia arriba.
            const apex = y - gap
            ctx.moveTo(x, apex)
            ctx.lineTo(x - size, apex - size * 1.4)
            ctx.lineTo(x + size, apex - size * 1.4)
          } else {
            // ▲ bajo el low: ápice apuntando a la vela, base hacia abajo.
            const apex = y + gap
            ctx.moveTo(x, apex)
            ctx.lineTo(x - size, apex + size * 1.4)
            ctx.lineTo(x + size, apex + size * 1.4)
          }
          ctx.closePath()
          ctx.fill()
          continue
        }

        const x0 = xOf(seg.time0).x
        const x1 = xOf(seg.time1).x
        if (!Number.isFinite(x0) || !Number.isFinite(x1)) continue

        if (seg.kind === 'band') {
          const yUpper = pc(seg.upper)
          const yLower = pc(seg.lower)
          if (yUpper == null || yLower == null || Number.isNaN(yUpper) || Number.isNaN(yLower)) continue
          const yTop = Math.min(yUpper, yLower)
          const yBottom = Math.max(yUpper, yLower)
          const left = Math.min(x0, x1)
          const histRight = Math.max(x0, x1)
          // Proyección: de la última vela cerrada al FIN DEL RIGHTOFFSET del
          // chart — time2 = lastCandle + rightOffsetBars × barSeconds (mismo
          // límite que las zonas OB/FVG), resuelto con la misma extrapolación
          // por tiempo. Anclado: al panear al vacío futuro la banda TERMINA
          // ahí; no crece con el viewport.
          const right = seg.project && seg.time2 != null ? xOf(seg.time2).x : histRight
          if (!Number.isFinite(right) || right <= left) continue

          // Fill completo (histórico + proyección) — alpha bajo: las bandas
          // LP son contexto y LWC 4.2.3 pinta las custom series SIEMPRE por
          // encima de las velas (no hay reorden de series en el pane).
          ctx.fillStyle = this._withAlpha(seg.color || '#B08D57', seg.fillAlpha ?? 0.1)
          ctx.fillRect(left, yTop, right - left, Math.max(1, yBottom - yTop))

          // Borde fino: textura del estado/kind en el tramo histórico,
          // `projectStyle` en la proyección. Sin proyección se cierra a la
          // derecha (zona terminada, p. ej. pool barrido en su sweep_time).
          const borderColor = this._withAlpha(seg.color || '#B08D57', seg.borderAlpha ?? 0.55)
          ctx.strokeStyle = borderColor
          ctx.lineWidth = seg.borderWidth ?? 1
          const histStyle = this._dashStyle(seg.borderStyle || 'solid')
          const projStyle = this._dashStyle(seg.projectStyle || 'dashed')

          const hline = (xa, xb, y, dash) => {
            if (xb - xa < 0.5) return
            ctx.beginPath()
            ctx.setLineDash(dash)
            ctx.moveTo(xa, y)
            ctx.lineTo(xb, y)
            ctx.stroke()
          }
          const projLeft = seg.project ? histRight : right
          // Tramo histórico: superior, inferior y cierre izquierdo.
          hline(left, histRight, yTop, histStyle)
          hline(left, histRight, yBottom, histStyle)
          ctx.beginPath()
          ctx.setLineDash(histStyle)
          ctx.moveTo(left, yTop)
          ctx.lineTo(left, yBottom)
          ctx.stroke()
          if (seg.project) {
            // Proyección: solo superior e inferior hasta el límite del offset.
            hline(projLeft, right, yTop, projStyle)
            hline(projLeft, right, yBottom, projStyle)
          } else {
            // Cierre derecho de la zona terminada.
            ctx.beginPath()
            ctx.setLineDash(histStyle)
            ctx.moveTo(right, yTop)
            ctx.lineTo(right, yBottom)
            ctx.stroke()
          }
          ctx.setLineDash([])
          continue
        }

        const y = pc(seg.price)
        if (y == null || Number.isNaN(y)) continue
        if (y < -paneHeight || y > paneHeight * 2) continue

        ctx.beginPath()
        ctx.strokeStyle = this._withAlpha(seg.color || '#B08D57', seg.alpha ?? 1)
        ctx.lineWidth = seg.width ?? 1
        ctx.setLineDash(this._dashStyle(seg.lineStyle || 'solid'))
        ctx.moveTo(x0, y)
        ctx.lineTo(x1, y)
        ctx.stroke()
        ctx.setLineDash([])
      }
    })
  }

  _dashStyle(style) {
    switch (style) {
      case 'dashed': return [6, 3]
      case 'dotted': return [2, 2]
      default: return []
    }
  }

  _withAlpha(color, alpha) {
    if (typeof color !== 'string') return color
    if (color.startsWith('rgba')) {
      return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/, `rgba($1, $2, $3, ${alpha})`)
    }
    if (color.startsWith('rgb(')) {
      const rgb = color.match(/\d+/g)
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    }
    if (color.startsWith('#')) {
      const hex = color.replace('#', '')
      if (hex.length === 6) {
        const bigint = parseInt(hex, 16)
        const r = (bigint >> 16) & 255
        const g = (bigint >> 8) & 255
        const b = bigint & 255
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
    }
    return color
  }
}

export class SegmentsSeries {
  constructor() {
    this._renderer = new SegmentsPaneRenderer()
  }

  // API extra fuera del contrato LWC: el indicador inyecta los items a pintar.
  setSegments(segments) {
    this._renderer.setSegments(segments)
  }

  // Base temporal (segundos por vela) para la pps canónica barSpacing/barSeconds.
  setTimeBase(timeBase) {
    this._renderer.setTimeBase(timeBase)
  }

  // Ancla de precio: el priceToCoordinate de la serie de velas (ronda 3.3).
  setPriceFn(fn) {
    this._renderer.setPriceFn(fn)
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    // Contrato LWC: valores de precio finitos para cada fila no-whitespace.
    // La fila siempre trae `price` (la asigna el indicador desde un segmento
    // que toca ese tiempo); el fallback evita undefined si algo se cuela.
    const p = plotRow?.price
    return Number.isFinite(p) ? [p] : [0]
  }

  isWhitespace(data) {
    return data == null || data._t == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    }
  }
}
